export const SOCIETY_TYPES = [
  'OPEN_PLOTS',
  'VILLAS',
  'HOUSES',
  'FLATS',
  'MIXED',
  'FARM_PLOTS',
  'FARM_HOUSES',
  'GATED_COMMUNITY',
  'APARTMENTS',
  'MIXED_COMMUNITY',
  'OTHER',
] as const;

export type SocietyType = (typeof SOCIETY_TYPES)[number];

export interface SocietyTypeOption {
  value: SocietyType;
  label: string;
  description: string;
}

export const SOCIETY_TYPE_OPTIONS: SocietyTypeOption[] = [
  { value: 'OPEN_PLOTS', label: 'Open Plots', description: 'Layout plots, plotted developments, survey sectors' },
  { value: 'VILLAS', label: 'Villas', description: 'Individual villas, bungalows, row houses, phases' },
  { value: 'HOUSES', label: 'Houses', description: 'Independent houses, residential colony blocks' },
  { value: 'FLATS', label: 'Flats', description: 'Multi-storey building flats / blocks' },
  { value: 'MIXED', label: 'Mixed', description: 'Combination of flats, villas, plots, and houses' },
  { value: 'FARM_PLOTS', label: 'Farm Plots', description: 'Agricultural / farmland layout plots & boundary surveys' },
  { value: 'FARM_HOUSES', label: 'Farm Houses', description: 'Agricultural estates with farmhouse villas' },
  { value: 'GATED_COMMUNITY', label: 'Gated Community', description: 'Mixed residential gated communities' },
];

export interface SocietyTypeFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  helperText?: string;
  gridSpan?: number;
}

export interface SocietyTypeConfig {
  id: string;
  code?: string;
  name: string;
  singularLabel: string;
  pluralLabel: string;
  shortLabel: string;
  propertyNumberLabel: string;
  addressLabel: string;
  isMixed?: boolean;
  allowedPropertyTypes?: string[];
  fields: SocietyTypeFieldConfig[];
  features: {
    hasFloor?: boolean;
    hasTower?: boolean;
    hasPhase?: boolean;
    hasBlock?: boolean;
    hasBuilding?: boolean;
    hasSurveyNumber?: boolean;
    hasPlotArea?: boolean;
    hasBuiltUpArea?: boolean;
    hasCarpetArea?: boolean;
    hasFacing?: boolean;
    hasRoadWidth?: boolean;
    hasParking?: boolean;
    hasRoadAccess?: boolean;
    isMixed?: boolean;
  };
  tableColumns: Array<{
    id: string;
    label: string;
    align?: 'left' | 'center' | 'right';
  }>;
  cardFields: string[];
  dashboardMetrics: {
    total: string;
    occupied: string;
    vacant: string;
    ownerOccupied: string;
    rented: string;
  };
}

export const FACING_OPTIONS = [
  { value: 'North', label: 'North' },
  { value: 'South', label: 'South' },
  { value: 'East', label: 'East' },
  { value: 'West', label: 'West' },
  { value: 'North-East', label: 'North-East' },
  { value: 'North-West', label: 'North-West' },
  { value: 'South-East', label: 'South-East' },
  { value: 'South-West', label: 'South-West' },
];

export const SOCIETY_TYPE_CONFIGS: Record<string, SocietyTypeConfig> = {
  OPEN_PLOTS: {
    id: 'OPEN_PLOTS',
    name: 'Open Plots',
    singularLabel: 'Plot',
    pluralLabel: 'Plots',
    shortLabel: 'Plt',
    propertyNumberLabel: 'Plot Number',
    addressLabel: 'Sector / Survey / Plot',
    features: {
      hasPhase: true,
      hasBlock: true,
      hasSurveyNumber: true,
      hasPlotArea: true,
      hasFacing: true,
      hasRoadWidth: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Plot Number', type: 'text', required: true, placeholder: 'e.g. Plot 102, P-45', gridSpan: 6 },
      { name: 'surveyNumber', label: 'Survey / Khasra Number', type: 'text', placeholder: 'e.g. Survey 45/2A', gridSpan: 6 },
      { name: 'phase', label: 'Phase / Sector', type: 'text', placeholder: 'e.g. Sector 4, Phase 1', gridSpan: 6 },
      { name: 'block', label: 'Block / Pocket', type: 'text', placeholder: 'e.g. Block C, Pocket 2', gridSpan: 6 },
      { name: 'plotArea', label: 'Plot Area (sq.yd)', type: 'number', placeholder: 'e.g. 200 sq.yd', gridSpan: 4 },
      { name: 'facing', label: 'Facing', type: 'select', options: FACING_OPTIONS, gridSpan: 4 },
      { name: 'roadWidth', label: 'Road Width (ft)', type: 'text', placeholder: 'e.g. 40 ft, 60 ft', gridSpan: 4 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Plot No.' },
      { id: 'surveyNumber', label: 'Survey No.' },
      { id: 'plotArea', label: 'Plot Area' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'surveyNumber', 'plotArea', 'facing', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Plots',
      occupied: 'Sold Plots',
      vacant: 'Available Plots',
      ownerOccupied: 'Constructed',
      rented: 'Vacant Plots',
    },
  },

  VILLAS: {
    id: 'VILLAS',
    name: 'Villas',
    singularLabel: 'Villa',
    pluralLabel: 'Villas',
    shortLabel: 'Vla',
    propertyNumberLabel: 'Villa Number',
    addressLabel: 'Phase / Block / Villa',
    features: {
      hasPhase: true,
      hasBlock: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
      hasFacing: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Villa Number', type: 'text', required: true, placeholder: 'e.g. Villa 24, V-108', gridSpan: 6 },
      { name: 'phase', label: 'Phase', type: 'text', placeholder: 'e.g. Phase 1, Phase 2', gridSpan: 6 },
      { name: 'block', label: 'Block / Avenue', type: 'text', placeholder: 'e.g. Block B, Palm Avenue', gridSpan: 6 },
      { name: 'facing', label: 'Facing', type: 'select', options: FACING_OPTIONS, gridSpan: 6 },
      { name: 'plotArea', label: 'Plot Area (sq.yd / sq.ft)', type: 'number', placeholder: 'e.g. 300 sq.yd', gridSpan: 4 },
      { name: 'builtUpArea', label: 'Built-up Area (sq.ft)', type: 'number', placeholder: 'e.g. 3200 sq.ft', gridSpan: 4 },
      { name: 'parking', label: 'Car Parking Spots', type: 'text', placeholder: 'e.g. 2 Covered Cars', gridSpan: 4 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Villa No.' },
      { id: 'phase', label: 'Phase' },
      { id: 'block', label: 'Block' },
      { id: 'owner', label: 'Owner' },
      { id: 'residents', label: 'Residents' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'phase', 'block', 'plotArea', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Villas',
      occupied: 'Occupied Villas',
      vacant: 'Vacant Villas',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Villas',
    },
  },

  HOUSES: {
    id: 'HOUSES',
    name: 'Houses',
    singularLabel: 'House',
    pluralLabel: 'Houses',
    shortLabel: 'Hse',
    propertyNumberLabel: 'House Number',
    addressLabel: 'Street / Block / House',
    features: {
      hasBlock: true,
      hasFloor: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
      hasFacing: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'House Number', type: 'text', required: true, placeholder: 'e.g. House 14, H-202', gridSpan: 6 },
      { name: 'block', label: 'Street / Lane / Block', type: 'text', placeholder: 'e.g. Street 4, Lane B', gridSpan: 6 },
      { name: 'floor', label: 'Floors (Storeys)', type: 'number', placeholder: 'e.g. 1, 2, G+1', gridSpan: 4 },
      { name: 'facing', label: 'Facing', type: 'select', options: FACING_OPTIONS, gridSpan: 4 },
      { name: 'plotArea', label: 'Plot Area (sq.yd)', type: 'number', placeholder: 'e.g. 250 sq.yd', gridSpan: 4 },
      { name: 'builtUpArea', label: 'Built-up Area (sq.ft)', type: 'number', placeholder: 'e.g. 2200 sq.ft', gridSpan: 6 },
      { name: 'parking', label: 'Parking Slot / Garage', type: 'text', placeholder: 'e.g. 1 Car Garage', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'House No.' },
      { id: 'block', label: 'Street / Block' },
      { id: 'builtUpArea', label: 'Built-up Area' },
      { id: 'owner', label: 'Owner' },
      { id: 'residents', label: 'Residents' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'block', 'plotArea', 'builtUpArea', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Houses',
      occupied: 'Occupied Houses',
      vacant: 'Vacant Houses',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Houses',
    },
  },

  FLATS: {
    id: 'FLATS',
    name: 'Flats',
    singularLabel: 'Flat',
    pluralLabel: 'Flats',
    shortLabel: 'Flt',
    propertyNumberLabel: 'Flat Number',
    addressLabel: 'Building / Block / Flat',
    features: {
      hasBlock: true,
      hasFloor: true,
      hasBuilding: true,
      hasCarpetArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Flat Number', type: 'text', required: true, placeholder: 'e.g. 101, 204B', gridSpan: 6 },
      { name: 'building', label: 'Building / Tower', type: 'text', placeholder: 'Select or enter Tower / Block', gridSpan: 6 },
      { name: 'floor', label: 'Floor', type: 'number', placeholder: 'e.g. 1, 4', gridSpan: 4 },
      { name: 'parking', label: 'Parking Slot', type: 'text', placeholder: 'e.g. P-12, B1-04', gridSpan: 4 },
      { name: 'carpetArea', label: 'Carpet Area (sq.ft)', type: 'number', placeholder: 'e.g. 850', gridSpan: 4 },
      { name: 'builtUpArea', label: 'Built-up Area (sq.ft)', type: 'number', placeholder: 'e.g. 1100', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Flat No.' },
      { id: 'block', label: 'Block' },
      { id: 'floor', label: 'Floor' },
      { id: 'owner', label: 'Owner' },
      { id: 'residents', label: 'Residents' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'block', 'floor', 'ownerName', 'builtUpArea'],
    dashboardMetrics: {
      total: 'Total Flats',
      occupied: 'Occupied Flats',
      vacant: 'Vacant Flats',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Flats',
    },
  },

  MIXED: {
    id: 'MIXED',
    name: 'Mixed',
    singularLabel: 'Property',
    pluralLabel: 'Properties',
    shortLabel: 'Prop',
    propertyNumberLabel: 'Unit Number',
    addressLabel: 'Sector / Block / Unit',
    isMixed: true,
    allowedPropertyTypes: ['flat', 'villa', 'plot', 'house', 'farm_house', 'other'],
    features: {
      isMixed: true,
      hasPhase: true,
      hasBlock: true,
      hasFloor: true,
      hasTower: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
    },
    fields: [
      {
        name: 'propertyType',
        label: 'Property Type',
        type: 'select',
        required: true,
        options: [
          { value: 'flat', label: 'Flat' },
          { value: 'villa', label: 'Villa' },
          { value: 'house', label: 'House' },
          { value: 'plot', label: 'Open Plot' },
          { value: 'farm_house', label: 'Farm House' },
          { value: 'other', label: 'Other' },
        ],
        gridSpan: 6,
      },
      { name: 'propertyNumber', label: 'Unit / Property Number', type: 'text', required: true, placeholder: 'e.g. A-101, Villa 4', gridSpan: 6 },
      { name: 'phase', label: 'Phase / Sector', type: 'text', placeholder: 'e.g. Phase 2', gridSpan: 6 },
      { name: 'block', label: 'Block / Tower', type: 'text', placeholder: 'e.g. Block C', gridSpan: 6 },
      { name: 'builtUpArea', label: 'Area (sq.ft / sq.yd)', type: 'number', placeholder: 'e.g. 1800', gridSpan: 6 },
      { name: 'parking', label: 'Parking', type: 'text', placeholder: 'e.g. P-10', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Unit No.' },
      { id: 'propertyType', label: 'Type' },
      { id: 'block', label: 'Block' },
      { id: 'phase', label: 'Phase' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'propertyType', 'block', 'phase', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Units',
      occupied: 'Occupied Units',
      vacant: 'Vacant Units',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Units',
    },
  },

  FARM_PLOTS: {
    id: 'FARM_PLOTS',
    name: 'Farm Plots',
    singularLabel: 'Farm Plot',
    pluralLabel: 'Farm Plots',
    shortLabel: 'FP',
    propertyNumberLabel: 'Farm Plot Number',
    addressLabel: 'Zone / Survey / Plot',
    features: {
      hasPhase: true,
      hasSurveyNumber: true,
      hasPlotArea: true,
      hasRoadAccess: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Farm Plot Number', type: 'text', required: true, placeholder: 'e.g. Farm Plot 12, FP-08', gridSpan: 6 },
      { name: 'surveyNumber', label: 'Survey / Khasra Number', type: 'text', placeholder: 'e.g. Sy No. 145/2', gridSpan: 6 },
      { name: 'phase', label: 'Zone / Phase', type: 'text', placeholder: 'e.g. Zone A, Phase 1', gridSpan: 6 },
      { name: 'roadAccess', label: 'Road Access Width', type: 'text', placeholder: 'e.g. 40 ft Road', gridSpan: 6 },
      { name: 'plotArea', label: 'Land Area (Acres / Guntas / Sq.yd)', type: 'number', placeholder: 'e.g. 1.5 Acres', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Farm Plot No.' },
      { id: 'surveyNumber', label: 'Survey No.' },
      { id: 'plotArea', label: 'Land Area' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'surveyNumber', 'plotArea', 'roadAccess', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Farm Plots',
      occupied: 'Sold Farm Plots',
      vacant: 'Available Farm Plots',
      ownerOccupied: 'Developed',
      rented: 'Vacant Farm Plots',
    },
  },

  FARM_HOUSES: {
    id: 'FARM_HOUSES',
    name: 'Farm Houses',
    singularLabel: 'Farm House',
    pluralLabel: 'Farm Houses',
    shortLabel: 'FH',
    propertyNumberLabel: 'Farm House Number',
    addressLabel: 'Zone / Survey / Farm House',
    features: {
      hasPhase: true,
      hasSurveyNumber: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasRoadAccess: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Farm House Number', type: 'text', required: true, placeholder: 'e.g. Farm 12, FH-05', gridSpan: 6 },
      { name: 'surveyNumber', label: 'Survey / Land Number', type: 'text', placeholder: 'e.g. Sy No. 120/1', gridSpan: 6 },
      { name: 'phase', label: 'Zone / Phase', type: 'text', placeholder: 'e.g. Green Zone, Phase 1', gridSpan: 6 },
      { name: 'roadAccess', label: 'Road Access Width', type: 'text', placeholder: 'e.g. 60 ft Tar Road', gridSpan: 6 },
      { name: 'plotArea', label: 'Land Area (Acres / Guntas / Sq.yd)', type: 'number', placeholder: 'e.g. 2.5 Acres or 1200 sq.yd', gridSpan: 6 },
      { name: 'builtUpArea', label: 'Built-up Villa Area (sq.ft)', type: 'number', placeholder: 'e.g. 2800 sq.ft', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Farm House No.' },
      { id: 'surveyNumber', label: 'Survey No.' },
      { id: 'plotArea', label: 'Land Area' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'surveyNumber', 'plotArea', 'roadAccess', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Farm Houses',
      occupied: 'Occupied',
      vacant: 'Vacant',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented',
    },
  },

  GATED_COMMUNITY: {
    id: 'GATED_COMMUNITY',
    name: 'Gated Community',
    singularLabel: 'Property',
    pluralLabel: 'Properties',
    shortLabel: 'Prop',
    propertyNumberLabel: 'Property Number',
    addressLabel: 'Phase / Block / Property',
    features: {
      hasPhase: true,
      hasBlock: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Property Number', type: 'text', required: true, placeholder: 'e.g. Unit 101, Villa 12', gridSpan: 6 },
      {
        name: 'propertyType',
        label: 'Property Type',
        type: 'select',
        options: [
          { value: 'villa', label: 'Villa' },
          { value: 'flat', label: 'Flat' },
          { value: 'house', label: 'House' },
          { value: 'row_house', label: 'Row House' },
          { value: 'plot', label: 'Plot' },
        ],
        gridSpan: 6,
      },
      { name: 'phase', label: 'Phase / Sector', type: 'text', placeholder: 'e.g. Phase 1', gridSpan: 6 },
      { name: 'block', label: 'Block / Street', type: 'text', placeholder: 'e.g. Block A', gridSpan: 6 },
      { name: 'builtUpArea', label: 'Area (sq.ft)', type: 'number', placeholder: 'e.g. 2400', gridSpan: 6 },
      { name: 'parking', label: 'Parking Slots', type: 'text', placeholder: 'e.g. 2 Slots', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Property No.' },
      { id: 'propertyType', label: 'Type' },
      { id: 'block', label: 'Block' },
      { id: 'phase', label: 'Phase' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'propertyType', 'block', 'phase', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Properties',
      occupied: 'Occupied Properties',
      vacant: 'Vacant Properties',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Properties',
    },
  },

  // Backward compatibility
  APARTMENTS: {
    id: 'APARTMENTS',
    name: 'Apartments',
    singularLabel: 'Apartment',
    pluralLabel: 'Apartments',
    shortLabel: 'Apt',
    propertyNumberLabel: 'Apartment Number',
    addressLabel: 'Tower / Floor / Apartment',
    features: {
      hasTower: true,
      hasFloor: true,
      hasBuiltUpArea: true,
      hasParking: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Apartment Number', type: 'text', required: true, placeholder: 'e.g. 302, 1104', gridSpan: 6 },
      { name: 'tower', label: 'Tower / Wing', type: 'text', placeholder: 'e.g. Tower A, Pinnacle', gridSpan: 6 },
      { name: 'floor', label: 'Floor', type: 'number', placeholder: 'e.g. 3, 11', gridSpan: 4 },
      { name: 'unitType', label: 'Unit Type (BHK)', type: 'text', placeholder: 'e.g. 2 BHK, 3 BHK + Study', gridSpan: 4 },
      { name: 'parking', label: 'Parking Slot', type: 'text', placeholder: 'e.g. CP-24', gridSpan: 4 },
      { name: 'builtUpArea', label: 'Built-up Area (sq.ft)', type: 'number', placeholder: 'e.g. 1450', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Apartment No.' },
      { id: 'tower', label: 'Tower' },
      { id: 'floor', label: 'Floor' },
      { id: 'owner', label: 'Owner' },
      { id: 'residents', label: 'Residents' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'tower', 'floor', 'unitType', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Apartments',
      occupied: 'Occupied Apartments',
      vacant: 'Vacant Apartments',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Apartments',
    },
  },

  MIXED_COMMUNITY: {
    id: 'MIXED_COMMUNITY',
    name: 'Mixed Community',
    singularLabel: 'Property',
    pluralLabel: 'Properties',
    shortLabel: 'Prop',
    propertyNumberLabel: 'Property Number',
    addressLabel: 'Sector / Block / Unit',
    isMixed: true,
    allowedPropertyTypes: ['flat', 'villa', 'plot', 'farm_house', 'other'],
    features: {
      isMixed: true,
      hasPhase: true,
      hasBlock: true,
      hasFloor: true,
      hasTower: true,
      hasPlotArea: true,
      hasBuiltUpArea: true,
      hasParking: true,
    },
    fields: [
      {
        name: 'propertyType',
        label: 'Property Type',
        type: 'select',
        required: true,
        options: [
          { value: 'flat', label: 'Flat' },
          { value: 'villa', label: 'Villa' },
          { value: 'plot', label: 'Open Plot' },
          { value: 'farm_house', label: 'Farm House' },
          { value: 'other', label: 'Other' },
        ],
        gridSpan: 6,
      },
      { name: 'propertyNumber', label: 'Unit / Property Number', type: 'text', required: true, placeholder: 'e.g. A-101, Villa 4', gridSpan: 6 },
      { name: 'phase', label: 'Phase / Sector', type: 'text', placeholder: 'e.g. Phase 2', gridSpan: 6 },
      { name: 'block', label: 'Block / Tower', type: 'text', placeholder: 'e.g. Block C', gridSpan: 6 },
      { name: 'builtUpArea', label: 'Area (sq.ft / sq.yd)', type: 'number', placeholder: 'e.g. 1800', gridSpan: 6 },
      { name: 'parking', label: 'Parking', type: 'text', placeholder: 'e.g. P-10', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Unit No.' },
      { id: 'propertyType', label: 'Type' },
      { id: 'block', label: 'Block' },
      { id: 'phase', label: 'Phase' },
      { id: 'owner', label: 'Owner' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'propertyType', 'block', 'phase', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Units',
      occupied: 'Occupied Units',
      vacant: 'Vacant Units',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Units',
    },
  },

  OTHER: {
    id: 'OTHER',
    name: 'Other',
    singularLabel: 'Unit',
    pluralLabel: 'Units',
    shortLabel: 'Unit',
    propertyNumberLabel: 'Unit Number',
    addressLabel: 'Block / Unit',
    features: {
      hasBlock: true,
      hasBuiltUpArea: true,
    },
    fields: [
      { name: 'propertyNumber', label: 'Unit Number', type: 'text', required: true, placeholder: 'e.g. Unit 101', gridSpan: 6 },
      { name: 'block', label: 'Block / Section', type: 'text', placeholder: 'e.g. Section A', gridSpan: 6 },
      { name: 'builtUpArea', label: 'Area (sq.ft)', type: 'number', placeholder: 'e.g. 1200', gridSpan: 6 },
    ],
    tableColumns: [
      { id: 'propertyNumber', label: 'Unit No.' },
      { id: 'block', label: 'Block' },
      { id: 'owner', label: 'Owner' },
      { id: 'residents', label: 'Residents' },
      { id: 'status', label: 'Status' },
    ],
    cardFields: ['propertyNumber', 'block', 'ownerName'],
    dashboardMetrics: {
      total: 'Total Units',
      occupied: 'Occupied Units',
      vacant: 'Vacant Units',
      ownerOccupied: 'Owner Occupied',
      rented: 'Rented Units',
    },
  },
};

export function getSocietyTypeConfig(type?: string | null): SocietyTypeConfig {
  if (!type) return SOCIETY_TYPE_CONFIGS.FLATS;
  const upper = String(type).toUpperCase().replace(/\s+/g, '_');
  if (SOCIETY_TYPE_CONFIGS[upper]) return SOCIETY_TYPE_CONFIGS[upper];
  if (upper === 'APARTMENT') return SOCIETY_TYPE_CONFIGS.APARTMENTS;
  if (upper === 'VILLA') return SOCIETY_TYPE_CONFIGS.VILLAS;
  if (upper === 'PLOT' || upper === 'OPEN_PLOT') return SOCIETY_TYPE_CONFIGS.OPEN_PLOTS;
  if (upper === 'HOUSE') return SOCIETY_TYPE_CONFIGS.HOUSES;
  if (upper === 'FARM_PLOT') return SOCIETY_TYPE_CONFIGS.FARM_PLOTS;
  if (upper === 'FARM_HOUSE') return SOCIETY_TYPE_CONFIGS.FARM_HOUSES;
  if (upper === 'MIXED') return SOCIETY_TYPE_CONFIGS.MIXED;
  return SOCIETY_TYPE_CONFIGS.FLATS;
}
